import { noop } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ImportJobsPolling from '~/components/Admin/ImportJobsPolling/ImportJobsPolling';
import DocumentMetadataStatusBarList from '~/components/Admin/MoreSettingsPage/DocumentMetadataImportJob/DocumentMetadataStatusBarList';
import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

import { EVISORT_URL_REGEXP, KNOWLEDGE_CENTER_URL } from './constants';

export const DocumentsMetadataBulkUpload = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { params } = useRouting();
  const { clientId } = params;

  const configs = useSelector(
    slices.moreSettings.selectors.selectConfiguration,
  );

  return (
    <Accordion
      border="all"
      padding="all"
      itemGap="s"
      items={[
        {
          title: intl.formatMessage({
            id: 'DocumentsMetadataBulkUpload.title',
            defaultMessage: 'Documents Metadata Bulk Upload',
            description:
              'Title for the documents metadata bulk upload accordion section',
          }),
          isBordered: true,
          isExpanded: true,
          headerBorder: 'bottom',
          content: (
            <Layout preset="sections" p={6} align="flex-start">
              <ImportJobsPolling
                clientId={Number(clientId)}
                //@ts-ignore importedJobId is a null or string
                importedJobId={configs.importedJobId}
                jobsKind="documentMetadata"
                nullifyImportedJobIdProp={() =>
                  dispatch(
                    slices.moreSettings.actions.setConfigs({
                      ...configs,
                      importedJobId: null,
                    }),
                  )
                }
                handleAcknowledgeImportJob={noop}
              >
                <DocumentMetadataStatusBarList
                  key="document-metadata-status-bar-list"
                  importJobsData={[]}
                  handleAcknowledgeImportJobSuccessUpdate={noop}
                  clientId={Number(clientId)}
                />
              </ImportJobsPolling>

              <Layout direction="column" spacing={2}>
                <Text>
                  <FormattedMessage
                    id="DocumentsMetadataBulkUpload.uploadInstructions"
                    defaultMessage="Upload a .xls or .xlsx for the documents to update. Use <link>Search</link> to identify documents and fields names and Export to Excel to produce the file to import."
                    description="Instructions for uploading documents metadata file"
                    values={{
                      link: (chunks) => {
                        const isSearchDisabled =
                          EVISORT_URL_REGEXP.test('/search');
                        return (
                          <Link
                            disabled={isSearchDisabled}
                            tooltip={
                              isSearchDisabled
                                ? intl.formatMessage({
                                    id: 'DocumentsMetadataBulkUpload.searchDisabledTooltip',
                                    defaultMessage: 'Links are disabled',
                                    description:
                                      'Tooltip for disabled search link in documents metadata bulk upload',
                                  })
                                : undefined
                            }
                            pathname={'/search'}
                          >
                            {chunks}
                          </Link>
                        );
                      },
                    }}
                  />

                  <FormattedMessage
                    id="DocumentsMetadataBulkUpload.knowledgeCenterInfo"
                    defaultMessage=" For more information, visit the <a>Knowledge Center</a>."
                    description="Information text with link to Knowledge Center"
                    values={{
                      a: (chunks) => (
                        <a href={KNOWLEDGE_CENTER_URL}>{chunks}</a>
                      ),
                    }}
                  />
                </Text>
              </Layout>

              <Button
                text={intl.formatMessage({
                  id: 'DocumentsMetadataBulkUpload.uploadButton',
                  defaultMessage: 'Upload',
                  description:
                    'Button text for uploading documents metadata file',
                })}
                label="save-upload"
                onClick={() => {
                  dispatch(
                    slices.moreSettings.actions.setConfigs({
                      ...configs,
                      showImportWizard: true,
                    }),
                  );
                }}
                variant="primary"
                size="l"
              />
            </Layout>
          ),
        },
      ]}
      onToggle={() => {
        dispatch(
          slices.moreSettings.actions.setConfigs({
            ...configs,
            importedJobId: null,
          }),
        );
      }}
    />
  );
};
